import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AttendanceDay, DAY_STATUS, hhmm, localDate, minutesLabel, statusStyle } from '../payroll.api';

const HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Monday-first month grid, one coloured cell per day. */
export default function AttendanceCalendar({
  days,
  onDayClick,
}: {
  days: AttendanceDay[];
  onDayClick?: (day: AttendanceDay) => void;
}) {
  if (days.length === 0) return null;
  // Monday = 0 … Sunday = 6, from the first date's own weekday.
  const first = new Date(`${days[0].date}T00:00:00`);
  const lead = (first.getDay() + 6) % 7;
  const today = localDate();

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(92px, 1fr))', gap: 0.75, minWidth: 680 }}>
        {HEADS.map((h) => (
          <Typography key={h} variant='caption' fontWeight={700} textAlign='center' color='text.secondary'>
            {h}
          </Typography>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <Box key={`lead-${i}`} />
        ))}
        {days.map((d) => {
          const s = statusStyle(d.status);
          const label = d.status === 'HOLIDAY' && d.holidayName ? d.holidayName : s.label;
          const tip = [
            label,
            d.leaveType ? `Leave: ${d.leaveType}` : null,
            d.punchIn ? `In ${hhmm(d.punchIn)}` : null,
            d.punchOut ? `Out ${hhmm(d.punchOut)}` : null,
            d.workedMinutes ? `Worked ${minutesLabel(d.workedMinutes)}` : null,
            d.isLate ? 'Late' : null,
            d.override ? `Set by HR: ${statusStyle(d.override).label}` : null,
            d.remarks,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Tooltip key={d.date} title={tip} arrow>
              <Box
                onClick={onDayClick ? () => onDayClick(d) : undefined}
                sx={{
                  minHeight: 78,
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: s.bg,
                  color: s.fg,
                  border: '2px solid',
                  borderColor: d.date === today ? 'primary.main' : 'transparent',
                  cursor: onDayClick ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography variant='body2' fontWeight={700} sx={{ color: 'inherit' }}>
                    {Number(d.date.slice(8, 10))}
                  </Typography>
                  {d.isLate && (
                    <Tooltip title='Late'>
                      <Icon icon='mdi:clock-alert-outline' width={16} color='#C2410C' />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant='caption' fontWeight={600} sx={{ color: 'inherit', lineHeight: 1.2 }} noWrap>
                  {label}
                </Typography>
                {(d.punchIn || d.punchOut) && (
                  <Typography variant='caption' sx={{ color: 'inherit', opacity: 0.85 }}>
                    {hhmm(d.punchIn) || '--:--'} – {hhmm(d.punchOut) || '--:--'}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ mt: 1.5 }}>
        {(['PRESENT', 'HALF_DAY', 'ABSENT', 'PAID_LEAVE', 'UNPAID_LEAVE', 'HALF_LEAVE', 'HOLIDAY', 'WEEKLY_OFF', 'UPCOMING'] as const).map(
          (k) => (
            <Stack key={k} direction='row' alignItems='center' gap={0.5}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: DAY_STATUS[k].bg, border: '1px solid', borderColor: 'divider' }} />
              <Typography variant='caption'>{DAY_STATUS[k].label}</Typography>
            </Stack>
          )
        )}
      </Stack>
    </Box>
  );
}
